import { useEffect, useCallback } from 'react';
import { frontendAnalytics } from '../lib/firebase';

export const useAnalytics = () => {
  // Initialize analytics with user data from cookies or context
  useEffect(() => {
    // Try to get user info from cookies (if available in browser)
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);

      // Note: signed cookies can't be read directly from client-side
      // You might need to get user info from your app's state management
      // For now, we'll set it when we have it available
    }
  }, []);

  // Page view tracking
  const trackPageView = useCallback((pageName: string, additionalData?: Record<string, any>) => {
    frontendAnalytics.trackPageView(pageName, additionalData);
  }, []);

  // User interaction tracking
  const trackClick = useCallback((target: string, additionalData?: Record<string, any>) => {
    frontendAnalytics.trackUserInteraction('click', target, additionalData);
  }, []);

  const trackHover = useCallback((target: string, additionalData?: Record<string, any>) => {
    frontendAnalytics.trackUserInteraction('hover', target, additionalData);
  }, []);

  const trackFocus = useCallback((target: string, additionalData?: Record<string, any>) => {
    frontendAnalytics.trackUserInteraction('focus', target, additionalData);
  }, []);

  // Form tracking
  const trackFormSubmission = useCallback((formName: string, success: boolean, additionalData?: Record<string, any>) => {
    frontendAnalytics.trackFormSubmission(formName, success, additionalData);
  }, []);

  // API call tracking
  const trackAPICall = useCallback((
    method: string,
    endpoint: string,
    success: boolean,
    responseTime?: number,
    additionalData?: Record<string, any>
  ) => {
    frontendAnalytics.trackAPICall(method, endpoint, success, responseTime, additionalData);
  }, []);

  // Navigation tracking
  const trackNavigation = useCallback((from: string, to: string, method: 'click' | 'programmatic' = 'click') => {
    frontendAnalytics.trackNavigation(from, to, method);
  }, []);

  // Error tracking
  const trackError = useCallback((error: Error | string, context?: string, additionalData?: Record<string, any>) => {
    frontendAnalytics.trackError(error, context, additionalData);
  }, []);

  // Custom event tracking
  const trackCustomEvent = useCallback((eventName: string, properties?: Record<string, any>) => {
    frontendAnalytics.trackCustomEvent(eventName, properties);
  }, []);

  // Set user ID (call this when user logs in)
  const setUserId = useCallback((userId: string | null) => {
    frontendAnalytics.setUserId(userId);
  }, []);

  return {
    trackPageView,
    trackClick,
    trackHover,
    trackFocus,
    trackFormSubmission,
    trackAPICall,
    trackNavigation,
    trackError,
    trackCustomEvent,
    setUserId,
  };
};